const CLIENT_STEP_TAGS = new Set(["request", "validation"]);

export const jumpDesign: boolean = true;
export const jumpTasks: boolean = false;

export interface WorkflowStep {
    id?: number;
    tag?: string;
    step_key?: string;
    title: string;
    is_completed: boolean;
    step_order?: number;
    signed_at?: string | null;
    created_at?: string;
}

export const getCurrentWorkflowStep = (
    steps: WorkflowStep[]
): WorkflowStep | null => {
    const idx = steps.findIndex((step) => !step.is_completed);
    if (idx === -1) {
        return null;
    }

    if (idx > 0 && !steps[idx - 1]?.is_completed) {
        return null;
    }

    return steps[idx] ?? null;
};

export const getWorkflowStepProgress = (
    steps: WorkflowStep[],
    step?: WorkflowStep | null
): { current: number; total: number } | null => {
    if (steps.length === 0) {
        return null;
    }

    const targetStep = step ?? getCurrentWorkflowStep(steps);
    if (!targetStep) {
        return null;
    }

    const targetKey = targetStep.tag || targetStep.step_key || "";
    const idx = steps.findIndex(
        (item) => (item.tag || item.step_key || "") === targetKey
    );
    if (idx === -1) {
        return null;
    }

    return {
        current: idx + 1,
        total: steps.length,
    };
};

export const getNextWorkflowStep = (
    steps: WorkflowStep[],
    step: WorkflowStep
): WorkflowStep | null => {
    const stepKey = step.tag || step.step_key || "";
    const idx = steps.findIndex(
        (item) => (item.tag || item.step_key || "") === stepKey
    );
    if (idx === -1 || idx >= steps.length - 1) {
        return null;
    }

    const isRequirementsStep =
        stepKey === "request" || stepKey === "requirements";
    const tasksStep = steps.find((candidate) => {
        const tag = candidate.tag || candidate.step_key || "";

        return tag === "tasks" || tag === "Tareas";
    });
    const nextConfiguredStep = steps[idx + 1];
    const nextConfiguredTag =
        nextConfiguredStep?.tag || nextConfiguredStep?.step_key || "";

    if (
        isRequirementsStep &&
        tasksStep &&
        (nextConfiguredTag === "frontend" ||
            nextConfiguredTag === "Frontend")
    ) {
        return tasksStep;
    }

    for (let i = idx + 1; i < steps.length; i++) {
        const candidate = steps[i];
        const tag = candidate.tag || candidate.step_key || "";
        if (jumpDesign && (tag === "design" || tag === "Diseño")) {
            continue;
        }

        if (jumpTasks && (tag === "tasks" || tag === "Tareas")) {
            continue;
        }

        return candidate;
    }

    return null;
};

export const getPhaseStartDate = (
    steps: WorkflowStep[],
    step: WorkflowStep,
    developmentCreatedAt?: string | null
): string | null => {
    const stepKey = step.tag || step.step_key || "";
    const idx = steps.findIndex(
        (item) => (item.tag || item.step_key || "") === stepKey
    );
    if (idx <= 0) {
        return (
            developmentCreatedAt ||
            step.created_at ||
            steps[0]?.created_at ||
            null
        );
    }

    const previous = steps[idx - 1];
    return (
        previous?.signed_at ||
        previous?.created_at ||
        developmentCreatedAt ||
        null
    );
};

export const getStepDurationLabel = (
    steps: WorkflowStep[],
    step: WorkflowStep,
    developmentCreatedAt?: string | null,
    t?: (key: string, options?: Record<string, unknown>) => string
): string | null => {
    const isDone = step.is_completed;
    const stepKey = step.tag || step.step_key || "";
    const idx = steps.findIndex(
        (item) => (item.tag || item.step_key || "") === stepKey
    );
    const isCurrent =
        !isDone && (idx === 0 || Boolean(steps[idx - 1]?.is_completed));

    if (!isDone) {
        return null;
    }

    const startDateStr = getPhaseStartDate(steps, step, developmentCreatedAt);
    if (!startDateStr) {
        return null;
    }

    const start = new Date(startDateStr);
    if (Number.isNaN(start.getTime())) {
        return null;
    }

    let end: Date = new Date();
    if (isDone && step.signed_at) {
        const parsedEnd = new Date(step.signed_at);
        if (!Number.isNaN(parsedEnd.getTime())) {
            end = parsedEnd;
        }
    }

    const diffMs = Math.max(end.getTime() - start.getTime(), 0);
    const totalSeconds = Math.floor(diffMs / 1000);
    const d = Math.floor(totalSeconds / 86400);
    const h = Math.floor((totalSeconds % 86400) / 3600);
    const m = Math.floor((totalSeconds % 3600) / 60);
    const s = Math.floor(totalSeconds % 60);

    let result = "";
    if (d > 0) {
        result += `${d}d`;
    }
    if (h > 0) {
        result += `${h}h`;
    }
    if (m > 0) {
        result += `${m}m`;
    }
    if (s > 0) {
        result += `${s}s`;
    }

    if (!result) {
        return "0s";
    }

    return result;
};

export const WorkflowRole = {
    CLIENT: "client",
    IT: "it",
} as const;

export const getStepSigner = (stepTag?: string | null): string => {
    if (stepTag && CLIENT_STEP_TAGS.has(stepTag)) {
        return WorkflowRole.CLIENT;
    }

    return WorkflowRole.IT;
};

export const getStepRoleLabelKey = (stepTag?: string | null): string => {
    if (getStepSigner(stepTag) === WorkflowRole.CLIENT) {
        return "developments.detail.role_client";
    }

    return "developments.detail.role_it";
};

export const canUserSignWorkflowStep = (
    stepTag: string | undefined,
    options: {
        isCurrent: boolean;
        isEditor: boolean;
        isClient: boolean;
        isIt: boolean;
    }
): boolean => {
    const { isCurrent, isEditor, isClient, isIt } = options;

    if (!isCurrent || !isEditor) {
        return false;
    }

    if (jumpDesign && (stepTag === "design" || stepTag === "Diseño")) {
        return false;
    }

    if (jumpTasks && (stepTag === "tasks" || stepTag === "Tareas")) {
        return false;
    }

    if (getStepSigner(stepTag) === WorkflowRole.CLIENT) {
        return isClient || isIt;
    }

    return isIt;
};

export const canUserRevertWorkflowStep = (
    stepTag: string | undefined,
    options: {
        isCurrent: boolean;
        isEditor: boolean;
        isClient: boolean;
        isIt: boolean;
    }
): boolean => {
    const { isCurrent, isEditor, isClient, isIt } = options;

    if (isCurrent || !isEditor) {
        return false;
    }

    if (!isIt) {
        return false;
    }

    if (jumpDesign && (stepTag === "design" || stepTag === "Diseño")) {
        return false;
    }

    if (jumpTasks && (stepTag === "tasks" || stepTag === "Tareas")) {
        return false;
    }

    return true;
};
